import { useList } from '@refinedev/core';
import { useMemo } from 'react';

import { AvailableResources } from '@/config';
import { DEFAULT_CACHE_TIME, DEFAULT_STALE_TIME } from '@/constants';

import type { CropSeasonOption, SimplifiedCropSeasonResponse } from './types';

type Props = {
  regionId?: string;
  enabled?: boolean;
};

const useGetCropSeasonOptions = ({ regionId, enabled = true }: Props) => {
  const queryEnabled = Boolean(enabled && regionId);

  const { data, isLoading, fetchStatus, isPreviousData } =
    useList<SimplifiedCropSeasonResponse>({
      resource: AvailableResources.SIMPLIFIED_CYCLES,
      pagination: {
        mode: 'off'
      },
      meta: {
        params: {
          region_id: regionId
        }
      },
      queryOptions: {
        enabled: queryEnabled,
        // Session-stable: only a season write changes this list, and that
        // invalidates the cache (see `useInvalidateCropSeasonOptions`).
        staleTime: DEFAULT_STALE_TIME,
        // Must outlive the stale time, or an entry is dropped while still fresh.
        cacheTime: DEFAULT_CACHE_TIME,
        // Resource-prefixed so one invalidation clears every region; refine's
        // generated key is not. Replacing that key has two lasting costs:
        // refine can no longer auto-invalidate this query, and every value the
        // request varies by must be listed here or two queries share an entry.
        queryKey: [AvailableResources.SIMPLIFIED_CYCLES, regionId]
      }
    });

  const options: CropSeasonOption[] = useMemo(() => {
    if (!data?.data) {
      return [];
    }

    return data.data.map(season => ({
      label: season.name,
      value: season.id,
      cropId: season.crop_id,
      isActive: season.is_active
    }));
  }, [data]);

  return {
    options,
    /**
     * Gated on `queryEnabled`, because react-query reports a **disabled**
     * query as `isLoading: true` — it has no data and never will. Passing
     * that through would tell a caller to wait forever for a request that was
     * never made.
     */
    isLoading:
      queryEnabled &&
      (isLoading || (fetchStatus === 'fetching' && isPreviousData))
  };
};

export default useGetCropSeasonOptions;
